import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EntityType } from '../types';
import type { ChartDateBound, ChartDateFieldFilter } from '../chart/chartWhere.types';
import { boundToUi, uiSideToBound } from '../chart/chartDateBoundUtils';
import { generateRandomId, getRelativeTimeDay, sortEntryByRank } from '../common/util';
import { useGetRepositoryEntriesByFilterQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { isChartDateLikePropertyType } from '../chart/canvasChartData';
import {
	selectAllRepositoryEntries,
	selectEntryReadableValues,
} from '../containers/contentSlice';

dayjs.extend(relativeTime);

export interface ChartWhereFilterPropertyOption {
	slug: string;
	label: string;
	type: string;
	/** When true, date/datetime filters cannot use “Is unset”. */
	required?: boolean;
	/** Required when `type` is belongsTo. */
	targetRepositoryId?: string;
}

export type ChartWhereFilterRowKind = 'unset' | 'date' | 'belongsTo';

export type ChartWhereFilterRow =
	| { id: string; kind: 'unset'; slug: '' }
	| {
		id: string;
		kind: 'date';
		slug: string;
		dateFilterMode: 'range' | 'isUnset';
		startDateYmd: string;
		endDateYmd: string;
		useRelative: boolean;
	}
	| { id: string; kind: 'belongsTo'; slug: string; targetEntryId: string };

function newUnsetRow(): ChartWhereFilterRow {
	return { id: generateRandomId(), kind: 'unset', slug: '' };
}

function rowFromPropertyChoice(id: string, slug: string, type: string): ChartWhereFilterRow {
	if (type === EntityType.PropertyDefinitionBelongsTo) {
		return { id, kind: 'belongsTo', slug, targetEntryId: '' };
	}
	if (type === EntityType.PropertyDefinitionDate || type === EntityType.PropertyDefinitionDatetime) {
		return {
			id,
			kind: 'date',
			slug,
			dateFilterMode: 'range',
			startDateYmd: '',
			endDateYmd: '',
			useRelative: false,
		};
	}
	return { id, kind: 'unset', slug: '' };
}

function deserializeWhereToRows(
	whereJson: string,
	propertyBySlug: Map<string, ChartWhereFilterPropertyOption>,
): ChartWhereFilterRow[] {
	const raw = (whereJson ?? '').trim();
	if (!raw) {
		return [];
	}
	let doc: unknown;
	try {
		doc = JSON.parse(raw);
	} catch {
		return [];
	}
	if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
		return [];
	}
	const rows: ChartWhereFilterRow[] = [];
	for (const [slug, spec] of Object.entries(doc as Record<string, unknown>)) {
		if (!slug || spec === null || typeof spec !== 'object' || Array.isArray(spec)) {
			continue;
		}
		const prop = propertyBySlug.get(slug);
		const o = spec as Record<string, unknown>;
		if (
			prop?.type === EntityType.PropertyDefinitionBelongsTo
			&& typeof o.targetEntryId === 'string'
			&& o.targetEntryId.trim()
			&& o.start === undefined
			&& o.end === undefined
		) {
			rows.push({
				id: slug,
				kind: 'belongsTo',
				slug,
				targetEntryId: o.targetEntryId.trim(),
			});
			continue;
		}
		if (prop && isChartDateLikePropertyType(prop.type as EntityType)) {
			if (
				typeof o.targetEntryId === 'string'
				&& o.targetEntryId.trim()
				&& o.start === undefined
				&& o.end === undefined
			) {
				continue;
			}
			if (o.isUnset === true) {
				if (prop.required === true) {
					continue;
				}
				rows.push({
					id: slug,
					kind: 'date',
					slug,
					dateFilterMode: 'isUnset',
					startDateYmd: '',
					endDateYmd: '',
					useRelative: false,
				});
				continue;
			}
			const s = boundToUi(o.start);
			const e = boundToUi(o.end);
			if (!s.dateYmd && !e.dateYmd && !s.storedAsRelative && !e.storedAsRelative) {
				continue;
			}
			rows.push({
				id: slug,
				kind: 'date',
				slug,
				dateFilterMode: 'range',
				startDateYmd: s.dateYmd,
				endDateYmd: e.dateYmd,
				useRelative: s.storedAsRelative || e.storedAsRelative,
			});
		}
	}
	return rows;
}

function serializeRowsToWhere(
	rows: ChartWhereFilterRow[],
	propertyBySlug: Map<string, ChartWhereFilterPropertyOption>,
): string {
	const doc: Record<string, ChartDateFieldFilter | { targetEntryId: string }> = {};
	for (const row of rows) {
		if (row.kind === 'unset' || !row.slug.trim()) {
			continue;
		}
		const slug = row.slug.trim();
		const prop = propertyBySlug.get(slug);
		if (!prop) {
			continue;
		}
		if (row.kind === 'date') {
			if (row.dateFilterMode === 'isUnset') {
				doc[slug] = { isUnset: true };
			} else {
				const start = uiSideToBound(row.startDateYmd, row.useRelative);
				const end = uiSideToBound(row.endDateYmd, row.useRelative);
				if (start === undefined && end === undefined) {
					continue;
				}
				const entry: { start?: ChartDateBound; end?: ChartDateBound } = {};
				if (start !== undefined) {
					entry.start = start;
				}
				if (end !== undefined) {
					entry.end = end;
				}
				doc[slug] = entry;
			}
		} else if (row.kind === 'belongsTo') {
			const tid = row.targetEntryId.trim();
			if (!tid) {
				continue;
			}
			doc[slug] = { targetEntryId: tid };
		}
	}
	return Object.keys(doc).length ? JSON.stringify(doc) : '';
}

function datePropertyAllowsUnset(
	propertyBySlug: Map<string, ChartWhereFilterPropertyOption>,
	slug: string,
): boolean {
	const prop = propertyBySlug.get(slug);
	if (!prop || !isChartDateLikePropertyType(prop.type as EntityType)) {
		return false;
	}
	return prop.required !== true;
}

export function isChartWhereFilterableProperty(p: ChartWhereFilterPropertyOption): boolean {
	if (isChartDateLikePropertyType(p.type as EntityType)) {
		return true;
	}
	if (p.type === EntityType.PropertyDefinitionBelongsTo) {
		return Boolean((p.targetRepositoryId ?? '').trim());
	}
	return false;
}

export interface ChartWhereFilterBuilderProps {
	chartId: string;
	initialWhere: string;
	onChange: (nextJson: string) => void;
	properties: ChartWhereFilterPropertyOption[];
	disabled?: boolean;
	includeArchived: boolean;
	onIncludeArchivedChange: (value: boolean) => void;
	shouldAskAboutRelativeTime: boolean;
}

const ChartWhereFilterBuilder: React.FC<ChartWhereFilterBuilderProps> = ({
	chartId,
	initialWhere,
	onChange,
	properties,
	disabled = false,
	includeArchived,
	onIncludeArchivedChange,
	shouldAskAboutRelativeTime,
}) => {
	const filterable = useMemo(() => properties.filter(isChartWhereFilterableProperty), [properties]);
	const propertyBySlug = useMemo(
		() => new Map(filterable.map((p) => [p.slug, p])),
		[filterable]
	);
	const filterableSlugKey = useMemo(
		() => [...filterable.map((p) => p.slug)].sort().join('\0'),
		[filterable]
	);

	const [rows, setRows] = useState<ChartWhereFilterRow[]>(() =>
		deserializeWhereToRows(initialWhere, propertyBySlug));

	useEffect(() => {
		setRows(deserializeWhereToRows(initialWhere, propertyBySlug));
		// Intentionally avoid `initialWhere` here: while editing, parent `where`
		// echoes serialized output back into this component. Some transient UI
		// states (e.g. date mode switched to "range" with no bounds yet) serialize
		// to empty and would immediately erase the in-progress row.
	}, [chartId, filterableSlugKey, propertyBySlug]);

	const removeRow = useCallback(
		(rowId: string) => {
			setRows((prev) => {
				const next = prev.filter((r) => r.id !== rowId);
				onChange(serializeRowsToWhere(next, propertyBySlug));
				return next;
			});
		},
		[onChange, propertyBySlug]
	);

	const slugTakenElsewhere = useCallback(
		(slug: string, rowId: string) =>
			rows.some((r) => {
				if (r.kind === 'unset') {
					return false;
				}
				return r.id !== rowId && r.slug === slug && slug !== '';
			}),
		[rows]
	);

	const availableForRow = useCallback(
		(row: ChartWhereFilterRow) =>
			filterable.filter((p) => p.slug === (row.kind === 'unset' ? '' : row.slug) || !slugTakenElsewhere(p.slug, row.id)),
		[filterable, rows, slugTakenElsewhere]
	);

	const addRow = useCallback(() => {
		setRows((prev) => {
			const next = [...prev, newUnsetRow()];
			onChange(serializeRowsToWhere(next, propertyBySlug));
			return next;
		});
	}, [onChange, propertyBySlug]);

	const updateRow = useCallback(
		(rowId: string, updater: (r: ChartWhereFilterRow) => ChartWhereFilterRow) => {
			setRows((prev) => {
				const next = prev.map((r) => (r.id === rowId ? updater(r) : r));
				onChange(serializeRowsToWhere(next, propertyBySlug));
				return next;
			});
		},
		[onChange, propertyBySlug]
	);

	const linkedEntriesIncludeArchived = includeArchived !== false;

	return (
		<Box>
			<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Filter entries</Typography>
			{
				<>
					<FormControlLabel
						control={(
							<Checkbox
								size="small"
								checked={includeArchived}
								disabled={disabled}
								onChange={(_, checked) => onIncludeArchivedChange(checked)}
							/>
						)}
						label="Include archived"
						sx={{
							m: 0,
							alignItems: 'center',
							'& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
							userSelect: 'none'
						}}
					/>
					<Stack spacing={2} sx={{ mb: 1.5 }}>
						{rows.map((row) => (
							<ChartWhereFilterRowCard
								key={row.id}
								row={row}
								disabled={disabled}
								linkedEntriesIncludeArchived={linkedEntriesIncludeArchived}
								availableProperties={availableForRow(row)}
								propertyBySlug={propertyBySlug}
								onRemove={() => removeRow(row.id)}
								onChangeProperty={(slug) => {
									if (!slug) {
										updateRow(row.id, () => ({ id: row.id, kind: 'unset', slug: '' }));
										return;
									}
									const prop = propertyBySlug.get(slug);
									if (!prop) {
										return;
									}
									const next = rowFromPropertyChoice(row.id, slug, prop.type);
									updateRow(row.id, () => next);
								}}
								updateRow={updateRow}
								shouldAskAboutRelativeTime={shouldAskAboutRelativeTime}
							/>
						))}
					</Stack>
					<Button
						type="button"
						variant="outlined"
						size="small"
						startIcon={<AddIcon />}
						disabled={disabled}
						onClick={addRow}
					>
						New filter
					</Button>
				</>
			}
		</Box>
	);
};

function ChartWhereFilterRowCard(props: {
	row: ChartWhereFilterRow;
	disabled: boolean;
	/** Passed to linked-repository entry picker; mirrors chart/query “Include archived”. */
	linkedEntriesIncludeArchived: boolean;
	availableProperties: ChartWhereFilterPropertyOption[];
	propertyBySlug: Map<string, ChartWhereFilterPropertyOption>;
	onRemove: () => void;
	onChangeProperty: (slug: string) => void;
	updateRow: (rowId: string, updater: (r: ChartWhereFilterRow) => ChartWhereFilterRow) => void;
	shouldAskAboutRelativeTime: boolean;
}) {
	const {
		row,
		disabled,
		linkedEntriesIncludeArchived,
		availableProperties,
		propertyBySlug,
		onRemove,
		onChangeProperty,
		updateRow,
		shouldAskAboutRelativeTime,
	} = props;

	const slugValue = row.kind === 'unset' ? '' : row.slug;
	const hasProperty = Boolean(slugValue.trim());

	return (
		<Box
			sx={{
				border: 1,
				borderColor: 'divider',
				borderRadius: 1,
				p: 1.5,
			}}
		>
			<Stack spacing={1.5}>
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<FormControl fullWidth size="small" disabled={disabled}>
						<InputLabel id={`cwf-prop-${row.id}`} shrink={true}>Property</InputLabel>
						<Select
							labelId={`cwf-prop-${row.id}`}
							label="Property"
							value={slugValue}
							displayEmpty
							onChange={(e) => onChangeProperty(String(e.target.value))}
						>
							<MenuItem value="">
								<em>Choose property…</em>
							</MenuItem>
							{availableProperties.map((p) => (
								<MenuItem key={p.slug} value={p.slug}>
									{p.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						type="button"
						size="small"
						color="inherit"
						disabled={disabled}
						onClick={onRemove}
						sx={{ minWidth: 0, px: 1 }}
						aria-label="Remove filter"
					>
						<DeleteOutlineIcon fontSize="small" />
					</Button>
				</Stack>
				{row.kind === 'date' && (
					<>
						{hasProperty && datePropertyAllowsUnset(propertyBySlug, row.slug) && (
							<FormControl fullWidth size="small" disabled={disabled}>
								<InputLabel id={`cwf-date-mode-${row.id}`} shrink>
									Match
								</InputLabel>
								<Select
									labelId={`cwf-date-mode-${row.id}`}
									label="Match"
									value={row.dateFilterMode}
									displayEmpty
									onChange={(e) => {
										const v = String(e.target.value) as 'range' | 'isUnset';
										updateRow(row.id, (r) => {
											if (r.kind !== 'date') {
												return r;
											}
											if (v === 'isUnset') {
												return {
													...r,
													dateFilterMode: 'isUnset',
													useRelative: false,
												};
											}
											return { ...r, dateFilterMode: 'range' };
										});
									}}
								>
									<MenuItem value="range">Choose dates</MenuItem>
									<MenuItem value="isUnset">Is unset</MenuItem>
								</Select>
							</FormControl>
						)}
						{row.dateFilterMode === 'range' && (
							<>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
									<DateYmdField
										label="Earliest"
										disabled={disabled || !hasProperty}
										dateYmd={row.startDateYmd}
										useRelative={row.useRelative}
										onDateYmd={(v) => {
											updateRow(row.id, (r) => {
												if (r.kind !== 'date') {
													return r;
												}
												return {
													...r,
													startDateYmd: v,
													useRelative: (Boolean(v.trim()) || Boolean(r.endDateYmd.trim()))
														? r.useRelative
														: false,
												};
											});
										}}
									/>
									<DateYmdField
										label="Latest"
										disabled={disabled || !hasProperty}
										dateYmd={row.endDateYmd}
										useRelative={row.useRelative}
										onDateYmd={(v) => {
											updateRow(row.id, (r) => {
												if (r.kind !== 'date') {
													return r;
												}
												return {
													...r,
													endDateYmd: v,
													useRelative: (Boolean(r.startDateYmd.trim()) || Boolean(v.trim()))
														? r.useRelative
														: false,
												};
											});
										}}
									/>
								</Stack>
								{shouldAskAboutRelativeTime && <FormControlLabel
									control={(
										<Checkbox
											size="small"
											checked={row.useRelative}
											disabled={disabled || !hasProperty}
											onChange={(_, checked) => {
												updateRow(row.id, (r) =>
													r.kind === 'date' ? { ...r, useRelative: checked } : r
												);
											}}
										/>
									)}
									label="Moving time window"
									sx={{ m: 0, alignItems: 'center', '& .MuiFormControlLabel-label': { fontSize: '0.8125rem'} }}
									style={{marginTop: 0}}
								/>}
							</>
						)}
					</>
				)}
				{row.kind === 'belongsTo' && (
					<BelongsToValueSelect
						rowId={row.id}
						disabled={disabled}
						hasProperty={hasProperty}
						includeArchived={linkedEntriesIncludeArchived}
						targetRepositoryId={
							propertyBySlug.get(row.slug)?.targetRepositoryId ?? ''
						}
						targetEntryId={row.targetEntryId}
						onTargetEntryId={(targetEntryId) => {
							updateRow(row.id, (r) =>
								r.kind === 'belongsTo' ? { ...r, targetEntryId } : r
							);
						}}
					/>
				)}
			</Stack>
		</Box>
	);
}

function DateYmdField(props: {
	label: string;
	disabled: boolean;
	dateYmd: string;
	useRelative: boolean;
	onDateYmd: (v: string) => void;
}) {
	const { label, disabled, dateYmd, useRelative, onDateYmd } = props;

	const relativePhrase = useMemo(() => {
		if (!useRelative || !dateYmd.trim()) {
			return '';
		}
		return getRelativeTimeDay(dateYmd);
	}, [dateYmd, useRelative]);

	const pickerValue = dateYmd.length >= 10 ? dateYmd.slice(0, 10) : '';

	return (
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<TextField
				size="small"
				fullWidth
				type="date"
				label={label}
				InputLabelProps={{ shrink: true }}
				value={pickerValue}
				onChange={(e) => onDateYmd(e.target.value)}
				disabled={disabled}
				helperText={useRelative && relativePhrase ? relativePhrase : undefined}
			/>
		</Box>
	);
}

function BelongsToValueSelect(props: {
	rowId: string;
	disabled: boolean;
	hasProperty: boolean;
	includeArchived: boolean;
	targetRepositoryId: string;
	targetEntryId: string;
	onTargetEntryId: (id: string) => void;
}) {
	const {
		rowId,
		disabled,
		hasProperty,
		includeArchived,
		targetRepositoryId,
		targetEntryId,
		onTargetEntryId,
	} = props;

	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const localRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const entryReadableValues = useAppSelector(selectEntryReadableValues);

	const { data: entriesData, isFetching } = useGetRepositoryEntriesByFilterQuery(
		{ repositoryId: targetRepositoryId, includeArchived },
		{ skip: !isAuthenticated || !targetRepositoryId.trim() }
	);

	const localEntryOptions = useMemo(
		() =>
			localRepositoryEntries
				.filter((entry) =>
					entry.repositoryId === targetRepositoryId
					&& (includeArchived || entry.isArchived !== true)
				)
				.map((entry) => ({
					id: entry.id,
					label: (entryReadableValues.get(entry.id) ?? '').trim() || entry.id,
				}))
				.sort(sortEntryByRank),
		[entryReadableValues, includeArchived, localRepositoryEntries, targetRepositoryId]
	);

	const entryOptions = useMemo(
		() => (
			isAuthenticated
				? (entriesData?.rows ?? []).map((e) => ({
					id: e.id,
					label: (e.label ?? '').trim() || e.id,
				}))
				: localEntryOptions
		),
		[entriesData?.rows, isAuthenticated, localEntryOptions]
	);

	const targetSelectDisabled = disabled || !hasProperty;

	return (
		<FormControl fullWidth size="small" disabled={targetSelectDisabled}>
			<InputLabel id={`cwf-entry-${rowId}`} shrink={true}>
				Linked entry
			</InputLabel>
			<Select
				labelId={`cwf-entry-${rowId}`}
				label="Linked entry"
				value={targetEntryId}
				displayEmpty
				onChange={(e) => onTargetEntryId(String(e.target.value))}
			>
				<MenuItem value="">
					<em>
						{!hasProperty ? 'Choose a property first' : isFetching ? 'Loading…' : 'Select entry'}
					</em>
				</MenuItem>
				{entryOptions.map((opt) => (
					<MenuItem key={opt.id} value={opt.id}>
						{opt.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}

export default ChartWhereFilterBuilder;
